// 设置 → 管理：测试 agent (src/devbots/, routes/devbots.ts): owners and admins of the Raft server spawn LLM-driven test
// agents into this office on deck-dev, pick what they do, and take them away. The section appears only when
// GET /api/devbots answers (the flag is on here and I may): production and everyone else never see it.
import { dropdown } from '/dropdown.js';
import { t, errorText } from '/i18n.js';

/** The words (public/locales: devbots.*). */
const T = {
  // cap_calls null: no call cap, only the USD one
  today: (u, n, max) => t(u.cap_calls == null ? 'devbots.todayNoCallCap' : 'devbots.today', { usd: u.usd.toFixed(4), cap: u.cap_usd, calls: u.calls, capCalls: u.cap_calls, n, max }),
  ttls: [30, 120, 480],
  mode: (m) => t(`devbots.mode.${m}`),
  game: (g) => t(`devbots.game.${g}`),
  status: (s) => t(`devbots.status.${s}`),
  /** A bot's last problem (a code from src/devbots/devbots.ts), in words. */
  err: (e) => ['cap', 'model_off', 'no_table', 'connect', 'internal'].includes(e) ? t(`devbots.botErr.${e}`) : e.startsWith('closed:') ? t('devbots.botErr.connect')
    : e.startsWith('model:') ? t('devbots.botErr.model', { code: e.slice(6) }) : t('devbots.botErr.other', { code: e }),
};
const CSS = `.dbpanel{display:flex;flex-direction:column;gap:6px;font-size:13px}
.dbpanel .row{display:flex;flex-wrap:wrap;align-items:center;gap:6px}
.dbpanel input,.dbpanel textarea{font:inherit;font-size:16px;padding:3px 6px;border:2px solid #1b1410;border-radius:10px;box-sizing:border-box;background:#fff}
.dbpanel textarea{width:100%;min-height:52px;resize:vertical}
.dbpanel input.num{width:60px}
.dbpanel input.nm{width:140px}
.dbpanel button.pill{font:inherit;font-size:13px;border:2px solid #1b1410;border-radius:999px;padding:4px 10px;background:#fff;cursor:pointer;white-space:nowrap}
.dbpanel button.pill.go{background:#FDD03B}
.dbpanel button.pill:disabled{opacity:.45;cursor:default}
.dbpanel .now{font-size:12px;color:#555}
.dbpanel .bots{display:flex;flex-direction:column;gap:4px;max-height:220px;overflow:auto}
.dbpanel .bot{display:flex;align-items:center;gap:6px;border:2px solid #1b1410;border-radius:10px;padding:3px 6px;background:#fff8e7}
.dbpanel .bot .who{flex:1;min-width:0}
.dbpanel .bot .who b{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dbpanel .bot .who span{font-size:11px;color:#6b5a44;display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.dbpanel .dd-field{width:auto;min-width:120px}`;

/** @param o {addSettingsSection, toast, room(): the office's room} */
export function initDevBots(o) {
  let state = null, box = null, timer = null, busy = false;
  const st = document.createElement('style'); st.textContent = CSS; document.head.appendChild(st);
  const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
  const pill = (text, go, cls = '') => { const b = el('button', 'pill ' + cls, text); b.type = 'button'; b.onclick = go; return b; };

  async function load() {
    try {
      const r = await fetch('/api/devbots', { credentials: 'same-origin', cache: 'no-store' });
      state = r.ok ? await r.json() : null;
    } catch { state = state ?? null; }
    return state;
  }
  async function post(body) {
    if (busy) return null;
    busy = true;
    try {
      const r = await fetch('/api/devbots', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => null);
      if (!r.ok) { o.toast(errorText('devbots.err', j?.error), null, 'robot'); return null; }
      return j;
    } finally { busy = false; await load(); if (box?.isConnected) list(box.querySelector('.bots')); }
  }

  // the form keeps what I typed between redraws (the list refreshes every few seconds while the panel is open)
  const form = { name: '', persona: '', model: null, count: 3, ttl: 120 };
  function panel(b) {
    box = b; b.replaceChildren();
    if (!state) return;
    const wrap = el('div', 'dbpanel');
    wrap.append(el('p', '', t('devbots.about')), el('p', 'now', T.today(state.today, state.total, state.max)));
    if (!state.models.length) wrap.append(el('p', 'now', t('devbots.noModel')));
    else {
      form.model = state.models.some((m) => m.id === form.model) ? form.model : state.models[0].id;
      const nm = el('input', 'nm'); nm.placeholder = t('devbots.name'); nm.maxLength = 12; nm.value = form.name; nm.setAttribute('aria-label', t('devbots.name')); nm.oninput = () => (form.name = nm.value);
      const per = el('textarea'); per.maxLength = 400; per.placeholder = state.default_persona; per.value = form.persona; per.setAttribute('aria-label', t('devbots.persona')); per.oninput = () => (form.persona = per.value);
      const model = dropdown({ label: t('devbots.model'), className: 'dd-field', options: state.models.map((m) => ({ value: m.id, text: m.label })), value: form.model, onChange: (v) => (form.model = v) });
      const n = el('input', 'num'); n.type = 'number'; n.min = '1'; n.max = '20'; n.value = String(form.count); n.inputMode = 'numeric'; n.setAttribute('aria-label', t('devbots.count')); n.oninput = () => (form.count = Number(n.value));
      const ttl = dropdown({ label: t('devbots.ttl'), className: 'dd-field', options: T.ttls.map((v) => ({ value: v, text: t(`devbots.ttl.${v}`) })), value: form.ttl, onChange: (v) => (form.ttl = v) });
      const go = pill(t('devbots.spawn'), async () => {
        const count = Math.floor(form.count);
        if (!(count >= 1 && count <= 20)) return n.focus();
        const r = await post({ op: 'spawn', room: o.room(), count, model: form.model, ttl_min: form.ttl, ...(form.name.trim() ? { name: form.name.trim() } : {}), ...(form.persona.trim() ? { persona: form.persona.trim() } : {}) });
        if (r?.made) o.toast(t('devbots.made', { n: r.made.length }), null, 'robot');
      }, 'go');
      wrap.append(el('h4', '', t('devbots.persona')), per, row(nm, model.el), row(el('span', '', t('devbots.count')), n, el('span', '', t('devbots.ttl')), ttl.el, go));
    }
    const bots = el('div', 'bots'); wrap.append(bots);
    // everyone at once: what they do, or a game to go and play
    const game = dropdown({ label: t('devbots.play'), className: 'dd-field', options: state.games.map((g) => ({ value: g, text: T.game(g) })), value: state.games[0], onChange: () => {} });
    const modes = state.modes.filter((m) => m !== 'game').map((m) => pill(T.mode(m), () => post({ op: 'mode', ids: 'all', mode: m })));
    wrap.append(row(el('span', '', t('devbots.all')), ...modes), row(game.el, pill(t('devbots.play'), () => post({ op: 'mode', ids: 'all', mode: 'game', game: game.value() })), pill(t('devbots.removeAll'), () => { if (confirm(t('devbots.confirmAll'))) post({ op: 'despawn', ids: 'all' }); })), el('p', 'now', t('devbots.cardsNote')));
    b.append(wrap);
    list(bots);
  }
  const row = (...kids) => { const r = el('div', 'row'); r.append(...kids); return r; };
  function list(bots) {
    if (!bots || !state) return;
    bots.replaceChildren();
    if (!state.bots.length) { bots.append(el('p', 'now', t('devbots.none'))); return; }
    for (const bt of state.bots) {
      const r = el('div', 'bot'), who = el('div', 'who');
      const doing = bt.mode === 'game' ? t('devbots.playing', { game: T.game(bt.game) }) : T.mode(bt.mode);
      who.append(el('b', '', bt.name), el('span', '', [T.status(bt.status), doing, state.models.find((m) => m.id === bt.model)?.label ?? bt.model, bt.room, t('devbots.left', { m: bt.left_min }), bt.error && T.err(bt.error)].filter(Boolean).join(' · ')));
      r.append(who, pill(t('devbots.remove'), () => post({ op: 'despawn', ids: [bt.id] })));
      bots.append(r);
    }
    const now = box?.querySelector('.now'); if (now) now.textContent = T.today(state.today, state.total, state.max);
  }

  // Shown only when the office says I may (flag on, owner or admin): checked once at start, then while it's open.
  load().then((s) => {
    if (!s) return;
    o.addSettingsSection({ id: 'devbots', title: () => t('devbots.title'), icon: 'robot', order: 90, render(b) {
      panel(b);
      load().then(() => { if (box === b && b.isConnected) list(b.querySelector('.bots')); });
      clearInterval(timer);
      timer = setInterval(async () => { if (!box?.isConnected) { clearInterval(timer); timer = null; return; } await load(); list(box.querySelector('.bots')); }, 4000);
    } });
  });
}
